import {ServiceBase} from "../ServiceBase";
import {CGPEventSubscription} from "../sdk/cgp-sdk.interface";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {GNWP_SOURCES} from "../../../sdk/src/components/external_links/external_links_consts";

enum LIMIT_NAMES {
    DEPOSIT_LIMIT = "DepositLimit",
    PLAYTIME_LIMIT = "PlayTimeLimit",
    BANKROLL_LIMIT = "BankrollLimit"
}

interface ILimitPeriod {
    unit?: string;
    value?: number;
    typeId?: number;
    currency?: string;
}

export class LimitsToasterService extends ServiceBase {

    private _limitsData: {
        [k in LIMIT_NAMES]: ILimitPeriod[];
    } = {
        DepositLimit: [],
        PlayTimeLimit: [],
        BankrollLimit: []
    };

    private _subscriptions: CGPEventSubscription[] = [];

    private _activeLimitToasters: number = 0;
    private _toasterDisplayDurationSec: number = 3;

    constructor(services: IServiceFactoryExtended) {
        super(services);

        this.reaction(() => this.services.user.isAuthorized, async () => {
            await this._getPlayerLimits();
            this._showLimitsToaster();

        }, {
            fireImmediately: true
        });

        this.services.sdk.events.onExternalLinkClosed (async (link: string) => {
            if (link === GNWP_SOURCES.MY_ACCOUNT) {
                await this._getPlayerLimits();
            }
        });

        this.services.sdk.events.onLiveGameSwitched(() => {
            this._showLimitsToaster();
        });
    }

    private _subscribe() {
        if (this._subscriptions.length === 0) {
            this._subscriptions.push(this.services.sdk.events.onGameClose(this._showLimitsToaster));
            this._subscriptions.push(this.services.sdk.events.onBeforeOAuthStateChanged(this._handleUserLogout));
        }
    }

    private _unsubscribe() {

        this._subscriptions.forEach(subscription => {
            subscription.off();
        });

        this._subscriptions = [];
    }

    private _getPlayerLimits = async () => {

        await this.services.user.waitForAutoLogin();

        if (!this.services.user.isAuthorized) {
            return;
        }

        if (!this.services.regulation.shouldShowPlayerLimitsToaster()) {
            return;
        }

        try {
            const limitsDataRaw = await this.services.api.userController.getPlayerLimits();

            const depositLimitRaw = limitsDataRaw.displayableLimits.find(limit => limit.limitName === LIMIT_NAMES.DEPOSIT_LIMIT);
            this._limitsData.DepositLimit = (depositLimitRaw && depositLimitRaw.limitPeriods) || [];

            const playTimeLimitRaw = limitsDataRaw.displayableLimits.find(limit => limit.limitName === LIMIT_NAMES.PLAYTIME_LIMIT);
            this._limitsData.PlayTimeLimit = (playTimeLimitRaw && playTimeLimitRaw.limitPeriods) || [];

            const bankrollLimitRaw = limitsDataRaw.displayableLimits.find(limit => limit.limitName === LIMIT_NAMES.BANKROLL_LIMIT);
            this._limitsData.BankrollLimit = (bankrollLimitRaw && bankrollLimitRaw.limitPeriods) || [];

            this._subscribe();

        } catch(error) {
            this.services.logger.error("LimitsToasterService: Failed to get the player limits.", error);
        }
    }

    private _handleUserLogout = () => {
        if (this.services.user.isAuthorized) {
            this._showLimitsToaster();
            this._unsubscribe();
        }
    }

    private _getIntervalLabelFromType(typeId: number): string {
        switch (typeId) {
            case 1:
                return this.services.translation.translate("PLAYER_LIMITS_TOASTER_DAILY_LABEL");
            case 2:
                return this.services.translation.translate("PLAYER_LIMITS_TOASTER_WEEKLY_LABEL");
            case 3:
                return this.services.translation.translate("PLAYER_LIMITS_TOASTER_MONTHLY_LABEL");
            default:
                return "";
        }
    }

    private _getFormatedTimeUnit(value: number, unit: string): string {

        if (unit === "min") {
            // value is always in minutes and in increments of 60 minutes
            return `${value / 60} ${this.services.translation.translate("PLAYER_LIMITS_TOASTER_HOUR_LABEL")}`;
        }

        return `${value} ${this.services.translation.translate("PLAYER_LIMITS_TOASTER_HOUR_LABEL")}`;
    }

    private _getFormatedDepositLimitForType(typeId: number): string {

        let formatedString = "";
        const period = this._limitsData.DepositLimit.find(period => period.typeId === typeId);

        if (period) {
            formatedString = `${this._getIntervalLabelFromType(typeId)}: ${this.services.currency.formatMoney(period.value, true)}`;
        }

        return formatedString;
    }

    private _getFormatedPlayTimeLimitForType(typeId: number): string {

        let formatedString = "";
        const period = this._limitsData.PlayTimeLimit.find(period => period.typeId === typeId);

        if (period) {
            formatedString = `${this._getIntervalLabelFromType(typeId)}: ${this._getFormatedTimeUnit(period.value, period.unit)}`;
        }

        return formatedString;
    }

    private _getFormatedWithSeparators(limits: string[]): string {

        let text = "";

        limits = limits.filter(limit => limit !== "");

        limits.forEach((limit, i) => {

            text += limit;

            if (limits[i + 1]) {
                text += " / ";
            }
        });

        return text;
    }

    private _displayDepositLimits() {

        if (this._limitsData.DepositLimit.length === 0) {
            return "";
        }

        return `<br>${this.services.translation.translate("PLAYER_LIMITS_TOASTER_DEPOSIT_LABEL")} - ${this._getFormatedWithSeparators([this._getFormatedDepositLimitForType(1), this._getFormatedDepositLimitForType(2), this._getFormatedDepositLimitForType(3)])}<br>`;
    }

    private _displayPlayTimeLimits() {

        if (this._limitsData.PlayTimeLimit.length === 0) {
            return "";
        }

        return `<br>${this.services.translation.translate("PLAYER_LIMITS_TOASTER_PLAY_TIME_LABEL")} - ${this._getFormatedWithSeparators([this._getFormatedPlayTimeLimitForType(1), this._getFormatedPlayTimeLimitForType(2), this._getFormatedPlayTimeLimitForType(3)])}<br>`;
    }

    private _displayBankrollLimits() {

        if (this._limitsData.BankrollLimit.length === 0) {
            return "";
        }

        return `<br>${this.services.translation.translate("PLAYER_LIMITS_TOASTER_BANKROLL_LABEL")} - ${this.services.currency.formatMoney(this._limitsData.BankrollLimit[0].value, true)}<br>`;
    }

    private _getFormatedLimitsText() {
        return `${this._displayDepositLimits()}${this._displayPlayTimeLimits()}${this._displayBankrollLimits()}`;
    }

    private _showLimitsToaster = () => {

        const hasData = this._limitsData.DepositLimit.length > 0 || this._limitsData.PlayTimeLimit.length > 0 || this._limitsData.BankrollLimit.length > 0;

        if (!this.services.regulation.shouldShowPlayerLimitsToaster() || this._activeLimitToasters > 0 || !hasData) {
            return;
        }

        // using timeout because there's an issue with the toaster, it won't show if it
        // is displayed while the layout is still loading eg: user logs out or on autologin
        setTimeout(() => {
            this.services.sdk.showToaster({
                Type: "timed_toaster",
                Title: this.services.translation.translate("PLAYER_LIMITS_TOASTER_TITLE"),
                SubTitle: this._getFormatedLimitsText(),
                DurationInSec: this._toasterDisplayDurationSec,
                MobileImage: "",
                PCImage: ""
            });
        }, 1000);

        this._activeLimitToasters++;

        setTimeout(() => {
            this._activeLimitToasters--;
        }, this._toasterDisplayDurationSec * 1000);
    }

    dispose() {
        super.dispose();
        this._unsubscribe();
    }
}
